import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { RENTAL_CATEGORIES, rateLabel } from "@/lib/rentals";
import { Search, MapPin, Home, Plus, Flame, BadgeCheck, ShieldCheck } from "lucide-react";

export default function Rentals() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (category) p.set("category", category);
    if (location.trim()) p.set("location", location.trim());
    try { const { data } = await api.get(`/rentals?${p}`); setItems(data || []); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10" data-testid="rentals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1">Rentals</p>
          <h1 className="h-display text-4xl sm:text-5xl">Rent anything, for any duration.</h1>
          <p className="text-[#4A4A4A] mt-2 max-w-xl">Homes, cars, cameras, studios — by the hour, day, week, or month. Booked and paid safely through Hoomanz.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/rentals/bookings" className="btn-ghost" data-testid="rentals-bookings-cta">My rentals</Link>
          <Link to="/rentals/mine" className="btn-ghost" data-testid="rentals-mine-cta">My listings</Link>
          <Link to="/rentals/new" className="btn-primary" data-testid="rentals-post-cta"><Plus size={14}/> List a rental</Link>
        </div>
      </div>

      <div className="card p-4 mt-7" data-testid="rentals-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-6 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Search rentals…" value={q}
              onChange={(e)=>setQ(e.target.value)} data-testid="rentals-search"/>
          </div>
          <div className="md:col-span-3 relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]"/>
            <input className="input pl-9" placeholder="Location" value={location}
              onChange={(e)=>setLocation(e.target.value)} onBlur={load} data-testid="rentals-location"/>
          </div>
          <select className="input md:col-span-3" value={category}
            onChange={(e)=>{ setCategory(e.target.value); setTimeout(load, 0); }} data-testid="rentals-category">
            <option value="">All categories</option>
            {RENTAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {err && <p className="text-sm text-[#B84B4B] mt-4">{err}</p>}
      {items === null && <p className="text-[#8A8A8A] mt-10">Loading…</p>}
      {items && items.length === 0 && (
        <div className="text-center py-20" data-testid="rentals-empty">
          <Home size={28} className="mx-auto text-[#0057FF] mb-3"/>
          <p className="text-[#4A4A4A]">Nothing for rent here yet.</p>
          <Link to="/rentals/new" className="btn-secondary inline-flex mt-4"><Plus size={14}/> List the first rental</Link>
        </div>
      )}

      {items?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="rentals-list">
          {items.map(i => (
            <button key={i.id} onClick={()=>nav(`/rentals/${i.id}`)} data-testid={`rental-tile-${i.id}`}
              className={`card overflow-hidden text-left hover:border-[#0A0A0A] transition-colors ${i.is_boosted ? "ring-1 ring-[#0057FF]/40" : ""}`}>
              <div className="aspect-[4/3] bg-[#F2F2F0]">
                {i.images?.[0] ? <img src={i.images[0]} className="w-full h-full object-cover" alt=""/> :
                  <div className="w-full h-full flex items-center justify-center text-[#8A8A8A]"><Home size={24}/></div>}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="font-medium leading-tight truncate flex-1">{i.title}</h3>
                  {i.is_boosted && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1"><Flame size={9}/> {i.boost_tier}</span>}
                </div>
                <p className="text-sm text-[#4A4A4A] mt-0.5">{i.category}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="font-medium text-lg">{rateLabel(i.available_rates, i.currency)}</p>
                  <span className="text-xs text-[#8A8A8A] inline-flex items-center gap-1"><MapPin size={11}/> {i.location || "—"}</span>
                </div>
                <p className="text-xs text-[#8A8A8A] mt-2 inline-flex items-center gap-1">
                  by <span className="font-medium text-[#0A0A0A]">{i.owner?.name}</span>
                  {i.owner?.verified && <BadgeCheck size={11} className="text-[#0A0A0A]"/>}
                  <span className="mx-1 opacity-50">·</span>
                  <ShieldCheck size={11}/> Paid on Hoomanz
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
